<script>
import CustomFieldService from '@/services/customFieldService';

export default {
    data() {
        return {
            showModal: false,
            isLoading: false,
            form: {
                idPublic: null,
                name: '',
                type: 1,
                color: '#556ee6',
                options: []
            },
            newOptionValue: '',
            newOptionColor: '#556ee6'
        };
    },
    computed: {
        fieldTypes() {
            return [
                { value: 1, text: this.$t('general.fieldTypeText') },
                { value: 2, text: this.$t('general.fieldTypeCheckbox') },
                { value: 3, text: this.$t('general.fieldTypeDropdown') }
            ];
        },
        isEditing() {
            return !!this.form.idPublic;
        },
        isDropdown() {
            return this.form.type === 3;
        },
        isCheckbox() {
            return this.form.type === 2;
        },
        canSave() {
            if (!this.form.name.trim()) return false;
            if (this.isDropdown && this.form.options.length === 0) return false;
            return true;
        },
        modalTitle() {
            return this.form.idPublic
                ? this.$t('general.editCustomColumn')
                : this.$t('general.addCustomColumn');
        }
    },
    methods: {
        show(customField = null) {
            this.resetForm();
            if (customField) {
                this.form.idPublic = customField.idPublic;
                this.form.name = customField.name;
                this.form.type = this.convertTypeToNumeric(customField.type);
                this.form.color = customField.color || '#556ee6';
                this.form.options = customField.customFieldOptions
                    ? customField.customFieldOptions.map(opt => ({
                        idPublic: opt.idPublic,
                        value: opt.name,
                        color: opt.color || '#556ee6',
                        order: opt.order || 0
                    }))
                    : [];
            }
            this.showModal = true;
        },
        convertTypeToNumeric(type) {
            if (typeof type === 'number') return type;
            const typeMap = {
                'Text': 1,
                'Checkbox': 2,
                'Dropdown': 3
            };
            return typeMap[type] || 1;
        },
        resetForm() {
            this.form = {
                idPublic: null,
                name: '',
                type: 1,
                color: '#556ee6',
                options: []
            };
            this.newOptionValue = '';
            this.newOptionColor = '#556ee6';
        },
        close() {
            this.showModal = false;
            this.$emit('closed');
        },
        addOption() {
            if (!this.newOptionValue.trim()) return;

            const newOption = {
                id: null,
                value: this.newOptionValue.trim(),
                color: this.newOptionColor,
                order: this.form.options.length
            };
            this.form.options.push(newOption);
            this.newOptionValue = '';
            this.newOptionColor = '#556ee6';
        },
        removeOption(index) {
            this.form.options.splice(index, 1);
            // Reorder remaining options
            this.form.options.forEach((opt, idx) => {
                opt.order = idx;
            });
        },
        async save() {
            if (!this.canSave) return;

            this.isLoading = true;

            try {
                const payload = {
                    idPublic: this.form.idPublic || '00000000-0000-0000-0000-000000000000',
                    name: this.form.name.trim(),
                    type: this.form.type,
                    color: this.isCheckbox ? this.form.color : null,
                    customFieldOptions: this.isDropdown
                        ? this.form.options.map(opt => ({
                            idPublic: opt.idPublic || '00000000-0000-0000-0000-000000000000',
                            name: opt.value || opt.name,
                            color: opt.color
                        }))
                        : null
                };

                if (this.form.idPublic) {
                    await CustomFieldService.update(this.form.idPublic, payload);
                } else {
                    await CustomFieldService.create(payload);
                }

                this.$emit('saved');
                this.close();
            } catch (error) {
                console.error('Error saving custom field:', error);
                this.$bvToast.toast(this.$t('general.errorSavingCustomField'), {
                    title: this.$t('general.error'),
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.isLoading = false;
            }
        },
        async confirmDelete() {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                this.$t('general.confirmDeleteCustomField'),
                {
                    title: this.$t('general.delete'),
                    okVariant: 'danger',
                    okTitle: this.$t('general.delete'),
                    cancelTitle: this.$t('general.cancel'),
                }
            );
            if (confirmed) {
                this.isLoading = true;
                try {
                    await CustomFieldService.delete(this.form.idPublic);
                    this.$emit('deleted');
                    this.close();
                } catch (error) {
                    console.error('Error deleting custom field:', error);
                    this.$bvToast.toast(this.$t('general.errorDeletingCustomField'), {
                        title: this.$t('general.error'),
                        variant: 'danger',
                        solid: true
                    });
                } finally {
                    this.isLoading = false;
                }
            }
        }
    }
};
</script>

<template>
    <b-modal
        v-model="showModal"
        :title="modalTitle"
        size="md"
        centered
        @hidden="close"
    >
        <div class="form-group">
            <label for="fieldName">{{ $t('general.name') }} *</label>
            <b-form-input
                id="fieldName"
                v-model="form.name"
                :placeholder="$t('general.customColumnNamePlaceholder')"
                required
            />
        </div>

        <div class="form-group">
            <label for="fieldType">{{ $t('general.type') }} *</label>
            <b-form-select
                id="fieldType"
                v-model="form.type"
                :options="fieldTypes"
                :disabled="isEditing"
            />
            <small v-if="isEditing" class="text-muted">
                {{ $t('general.typeCannotBeChanged') }}
            </small>
        </div>

        <div v-if="isCheckbox" class="form-group d-flex align-items-center" style="gap: 0.5rem;">
            <label class="mb-0">{{ $t('general.color') }}</label>
            <input
                type="color"
                v-model="form.color"
                class="color-picker-input"
            />
        </div>

        <div v-if="isDropdown" class="form-group">
            <label>{{ $t('general.dropdownOptions') }} *</label>

            <div class="options-list mb-2">
                <div
                    v-for="(option, index) in form.options"
                    :key="index"
                    class="d-flex align-items-center mb-1"
                >
                    <input
                        type="color"
                        v-model="option.color"
                        class="color-picker-input-sm mr-2"
                    />
                    <span class="flex-grow-1">{{ option.value }}</span>
                    <b-button
                        variant="outline-danger"
                        size="sm"
                        @click="removeOption(index)"
                    >
                        <i class="mdi mdi-delete"></i>
                    </b-button>
                </div>
            </div>

            <div class="d-flex align-items-center">
                <input
                    type="color"
                    v-model="newOptionColor"
                    class="color-picker-input-sm mr-2"
                />
                <b-form-input
                    v-model="newOptionValue"
                    :placeholder="$t('general.newOptionPlaceholder')"
                    class="mr-2"
                    @keyup.enter="addOption"
                />
                <b-button
                    variant="outline-primary"
                    @click="addOption"
                    :disabled="!newOptionValue.trim()"
                >
                    <i class="mdi mdi-plus"></i>
                </b-button>
            </div>

            <small v-if="form.options.length === 0" class="text-muted">
                {{ $t('general.addAtLeastOneOption') }}
            </small>
        </div>

        <template #modal-footer>
            <b-button
                v-if="form.idPublic"
                variant="danger"
                @click="confirmDelete"
                class="mr-auto"
            >
                <i class="mdi mdi-delete"></i>
                {{ $t('general.delete') }}
            </b-button>
            <b-button variant="secondary" @click="close">
                {{ $t('general.cancel') }}
            </b-button>
            <b-button
                variant="primary"
                @click="save"
                :disabled="!canSave || isLoading"
            >
                <span v-if="isLoading">
                    <b-spinner small></b-spinner>
                    {{ $t('general.saving') }}
                </span>
                <span v-else>
                    {{ $t('general.save') }}
                </span>
            </b-button>
        </template>
    </b-modal>
</template>

<style scoped>
.options-list {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    padding: 0.5rem;
}

.options-list:empty {
    display: none;
}

.color-picker-input {
    width: 50px;
    height: 35px;
    padding: 2px;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    cursor: pointer;
}

.color-picker-input-sm {
    width: 32px;
    height: 28px;
    padding: 1px;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    cursor: pointer;
}
</style>
